import { useState, useEffect } from "react";
import { api } from "@/App";
import { CalendarOff, Plus, Trash2, Calendar, RefreshCw, Wrench, Paintbrush } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

export default function ClosedDaysTab() {
  const [closedDays, setClosedDays] = useState([]);
  const [deptClosures, setDeptClosures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deptDialog, setDeptDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newDay, setNewDay] = useState({ date: "", label: "", recurring: false });
  const [newDept, setNewDept] = useState({ date: "", department: "mecanique", label: "" });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [daysRes, deptRes] = await Promise.all([
        api.get("/closed-days"),
        api.get("/dept-closures")
      ]);
      setClosedDays(daysRes.data);
      setDeptClosures(deptRes.data);
    } catch (e) { toast.error("Erreur chargement"); }
    setLoading(false);
  };

  const handleInitFeries = async () => {
    try {
      const res = await api.post("/closed-days/init-feries");
      toast.success(`${res.data.added} jours fériés ajoutés`);
      fetchData();
    } catch (e) { toast.error("Erreur"); }
  };

  const handleAddClosedDay = async () => {
    if (!newDay.date || !newDay.label) { toast.error("Date et libellé obligatoires"); return; }
    setSaving(true);
    try {
      await api.post("/closed-days", newDay);
      toast.success("Jour fermé ajouté");
      setDialogOpen(false);
      setNewDay({ date: "", label: "", recurring: false });
      fetchData();
    } catch (e) { toast.error("Erreur"); }
    setSaving(false);
  };

  const handleDeleteClosedDay = async (id) => {
    try { await api.delete(`/closed-days/${id}`); fetchData(); } catch (e) { toast.error("Erreur"); }
  };

  const handleAddDeptClosure = async () => {
    if (!newDept.date) { toast.error("Date obligatoire"); return; }
    setSaving(true);
    try {
      const label = newDept.label || (newDept.department === "mecanique" ? "Mécanique fermée" : "Carrosserie fermée");
      await api.post("/dept-closures", { ...newDept, label });
      toast.success("Fermeture ajoutée");
      setDeptDialog(false);
      setNewDept({ date: "", department: "mecanique", label: "" });
      fetchData();
    } catch (e) { toast.error("Erreur"); }
    setSaving(false);
  };

  const handleDeleteDeptClosure = async (id) => {
    try { await api.delete(`/dept-closures/${id}`); fetchData(); } catch (e) { toast.error("Erreur"); }
  };

  const recurring = closedDays.filter(d => d.recurring);
  const ponctuel = closedDays.filter(d => !d.recurring);
  const mecaClosures = deptClosures.filter(d => d.department === "mecanique").sort((a, b) => a.date.localeCompare(b.date));
  const carroClosures = deptClosures.filter(d => d.department === "carrosserie").sort((a, b) => a.date.localeCompare(b.date));

  if (loading) return <div className="flex justify-center py-8"><div className="spinner" /></div>;

  return (
    <div className="space-y-6">
      {/* Jours fériés */}
      <Card className="border-slate-200">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2"><CalendarOff className="h-5 w-5" />Jours fériés (récurrents)</CardTitle>
            <CardDescription>Fermé chaque année — tout le garage</CardDescription>
          </div>
          <Button variant="outline" onClick={handleInitFeries} data-testid="init-feries-btn">
            <RefreshCw className="h-4 w-4 mr-2" />Charger fériés français
          </Button>
        </CardHeader>
        <CardContent>
          {recurring.length === 0 ? (
            <p className="text-center text-slate-500 py-4">Aucun férié. Cliquez sur "Charger fériés français".</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {recurring.map(d => (
                <div key={d.id} className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
                  <Calendar className="h-4 w-4 text-red-500" />
                  <span className="font-medium text-sm text-red-800">{d.label}</span>
                  <Badge variant="outline" className="text-xs text-red-600 border-red-300">{d.date}</Badge>
                  <Button variant="ghost" size="icon" className="h-6 w-6 text-red-400 hover:text-red-600" onClick={() => handleDeleteClosedDay(d.id)}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Fermetures ponctuelles (tout le garage) */}
      <Card className="border-slate-200">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2"><Calendar className="h-5 w-5" />Fermetures ponctuelles</CardTitle>
            <CardDescription>Vacances, ponts... tout le garage fermé</CardDescription>
          </div>
          <Button onClick={() => setDialogOpen(true)} className="bg-red-600 hover:bg-red-700" data-testid="add-closed-day-btn">
            <Plus className="h-4 w-4 mr-2" />Ajouter
          </Button>
        </CardHeader>
        <CardContent>
          {ponctuel.length === 0 ? (
            <p className="text-center text-slate-500 py-4">Aucune fermeture ponctuelle</p>
          ) : (
            <div className="space-y-2">
              {ponctuel.sort((a, b) => (a.full_date || a.date).localeCompare(b.full_date || b.date)).map(d => (
                <div key={d.id} className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <CalendarOff className="h-4 w-4 text-amber-600" />
                    <div>
                      <p className="font-medium text-sm">{d.label}</p>
                      <p className="text-xs text-amber-600">{new Date(d.full_date || d.date).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</p>
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" className="text-red-400 hover:text-red-600" onClick={() => handleDeleteClosedDay(d.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Fermetures par service (méca / carro) */}
      <Card className="border-indigo-200 bg-indigo-50/20">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-indigo-800">
              <Wrench className="h-5 w-5" />
              Fermetures par service
            </CardTitle>
            <CardDescription>Fermer la mécanique OU la carrosserie un jour précis</CardDescription>
          </div>
          <Button onClick={() => setDeptDialog(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-dept-closure-btn">
            <Plus className="h-4 w-4 mr-2" />Ajouter
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Mécanique */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Wrench className="h-4 w-4 text-emerald-600" />
              <span className="text-sm font-bold text-emerald-700">Mécanique fermée</span>
              <Badge variant="outline" className="text-xs">{mecaClosures.length}</Badge>
            </div>
            {mecaClosures.length === 0 ? (
              <p className="text-xs text-slate-400 ml-6">Aucune fermeture mécanique</p>
            ) : (
              <div className="flex flex-wrap gap-2 ml-6">
                {mecaClosures.map(d => (
                  <div key={d.id} className="flex items-center gap-1.5 px-2 py-1 bg-emerald-50 border border-emerald-200 rounded text-xs">
                    <span className="font-medium">{new Date(d.date).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" })}</span>
                    {d.label !== "Mécanique fermée" && <span className="text-emerald-600">- {d.label}</span>}
                    <Button variant="ghost" size="icon" className="h-5 w-5 text-red-400" onClick={() => handleDeleteDeptClosure(d.id)}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* Carrosserie */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Paintbrush className="h-4 w-4 text-amber-600" />
              <span className="text-sm font-bold text-amber-700">Carrosserie fermée</span>
              <Badge variant="outline" className="text-xs">{carroClosures.length}</Badge>
            </div>
            {carroClosures.length === 0 ? (
              <p className="text-xs text-slate-400 ml-6">Aucune fermeture carrosserie</p>
            ) : (
              <div className="flex flex-wrap gap-2 ml-6">
                {carroClosures.map(d => (
                  <div key={d.id} className="flex items-center gap-1.5 px-2 py-1 bg-amber-50 border border-amber-200 rounded text-xs">
                    <span className="font-medium">{new Date(d.date).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" })}</span>
                    {d.label !== "Carrosserie fermée" && <span className="text-amber-600">- {d.label}</span>}
                    <Button variant="ghost" size="icon" className="h-5 w-5 text-red-400" onClick={() => handleDeleteDeptClosure(d.id)}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Dialog fermeture ponctuelle */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Fermeture ponctuelle (tout le garage)</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Date *</Label>
              <Input type="date" value={newDay.date} onChange={(e) => setNewDay({ ...newDay, date: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Libellé *</Label>
              <Input value={newDay.label} onChange={(e) => setNewDay({ ...newDay, label: e.target.value })} placeholder="Ex: Vacances, Pont..." />
            </div>
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
              <Switch checked={newDay.recurring} onCheckedChange={(v) => setNewDay({ ...newDay, recurring: v })} />
              <div>
                <Label className="font-medium">Récurrent chaque année</Label>
                <p className="text-xs text-slate-500">Pour les jours fériés fixes</p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleAddClosedDay} disabled={saving} className="bg-red-600 hover:bg-red-700">
              {saving ? <div className="spinner" /> : "Ajouter"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog fermeture par service */}
      <Dialog open={deptDialog} onOpenChange={setDeptDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Fermer un service pour un jour</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Date *</Label>
              <Input type="date" value={newDept.date} onChange={(e) => setNewDept({ ...newDept, date: e.target.value })} data-testid="dept-closure-date" />
            </div>
            <div className="space-y-2">
              <Label>Service à fermer</Label>
              <Select value={newDept.department} onValueChange={(v) => setNewDept({ ...newDept, department: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="mecanique">
                    <div className="flex items-center gap-2"><Wrench className="h-4 w-4 text-emerald-600" />Mécanique</div>
                  </SelectItem>
                  <SelectItem value="carrosserie">
                    <div className="flex items-center gap-2"><Paintbrush className="h-4 w-4 text-amber-600" />Carrosserie</div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Raison (optionnel)</Label>
              <Input value={newDept.label} onChange={(e) => setNewDept({ ...newDept, label: e.target.value })} placeholder="Ex: Formation, congé..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeptDialog(false)}>Annuler</Button>
            <Button onClick={handleAddDeptClosure} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-dept-closure-btn">
              {saving ? <div className="spinner" /> : "Fermer ce jour"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
